import { greekIt, transformToSeconds } from 'app/util/utils';
import { serializeBaselinePercentValue, deserializeBaselinePercentValue } from 'app/stores/alerting/policyUtils';
import { GREEK_METRICS } from 'app/util/constants';
import {
  ALERT_MANAGER_SEVERITY_TO_STANDARD_ALERT_SEVERITY,
  ALERT_MANAGER_ACTIVATION_MODES,
  RATIO_CONDITION_DIRECTIONS,
  COMPARISON_DIRECTIONS,
  THRESHOLD_CONDITION_TYPES
} from '@kentik/ui-shared/alerting/constants';

// DESERIALIZE START
function deserializeThresholdProperties(threshold, ruleLevel) {
  const { policyID, ...restThreshold } = threshold;

  if (ruleLevel) {
    restThreshold.ackRequired = ruleLevel.ack_required;
  }

  return {
    ...restThreshold,
    policy_id: policyID
  };
}

function getTimeoutFactor(value) {
  const HOUR_IN_MINS = 60;
  const DAY_IN_MINS = 24 * 60;

  if (!value) {
    return 1;
  }

  if (Number.isInteger(value / DAY_IN_MINS)) {
    return DAY_IN_MINS;
  }

  if (Number.isInteger(value / HOUR_IN_MINS)) {
    return HOUR_IN_MINS;
  }

  return 1;
}

function deserializeThresholdMitigations(threshold) {
  const { mitigations, ...restThreshold } = threshold;

  if (mitigations && mitigations.length > 0) {
    return {
      ...restThreshold,
      mitigations: mitigations.map((mitigation) => {
        const newMitigation = {
          ...mitigation,
          uiProperties: { applyTimeoutValue: 15, applyTimeoutFactor: 1, clearTimeoutValue: 15, clearTimeoutFactor: 1 }
        };

        // mitigations aren't as luxuriously structured as activate options are (there's no time unit)
        // so we're a bit more crude with what goes on in here until we can get rid of V3
        if (mitigation.applyStrategy === 'userAckOrTimeout') {
          if (mitigation.applyTimeout) {
            newMitigation.applyTimeout = parseInt(mitigation.applyTimeout.replace('s', ''), 10) / 60;
            newMitigation.uiProperties.applyTimeoutFactor = getTimeoutFactor(newMitigation.applyTimeout);
            newMitigation.uiProperties.applyTimeoutValue =
              newMitigation.applyTimeout / newMitigation.uiProperties.applyTimeoutFactor;
          }

          if (!Number.isFinite(newMitigation.applyTimeout)) {
            newMitigation.applyTimeout = 15;
          }
        }

        if (mitigation.clearStrategy === 'userAckOrTimeout') {
          if (mitigation.clearTimeout) {
            newMitigation.clearTimeout = parseInt(mitigation.clearTimeout.replace('s', ''), 10) / 60;
            newMitigation.uiProperties.clearTimeoutFactor = getTimeoutFactor(newMitigation.clearTimeout);
            newMitigation.uiProperties.clearTimeoutValue =
              newMitigation.clearTimeout / newMitigation.uiProperties.clearTimeoutFactor;
          }

          // safety fallback to default value
          if (!Number.isFinite(newMitigation.clearTimeout)) {
            newMitigation.clearTimeout = 15;
          }
        }

        if (newMitigation.clearTimeout === null) {
          delete newMitigation.clearTimeout;
        }

        if (newMitigation.applyTimeout === null) {
          delete newMitigation.applyTimeout;
        }

        return newMitigation;
      })
    };
  }

  return threshold;
}

// Input values in any unit,
// Output values in minute format
function deserializeGracePeriod(inactivityTimeUntilClear, defaultValue = 20) {
  if (!inactivityTimeUntilClear) {
    return defaultValue;
  }

  let gracePeriodValue = Number(inactivityTimeUntilClear.replace('s', '').replace('m', '').replace('h', ''));

  if (inactivityTimeUntilClear.endsWith('s')) {
    gracePeriodValue /= 60;
  } else if (inactivityTimeUntilClear.endsWith('h')) {
    gracePeriodValue /= 3600;
  }

  return gracePeriodValue || defaultValue;
}

function deserializeThresholdActivate(threshold, policy = {}, ruleLevel) {
  const { activationSettings: policyActivationSettings } = policy;
  const { activationSettings, ...restThreshold } = threshold;
  let { window = '', matchThreshold, inactivityTimeUntilClear } = activationSettings || {};

  // Set from rule if available
  if (ruleLevel?.rolling_window) {
    window = ruleLevel?.rolling_window?.window_length;
    inactivityTimeUntilClear = ruleLevel?.rolling_window?.grace_period;
    matchThreshold = ruleLevel?.rolling_window?.trigger_count;
  }

  const isToggleModePolicy = policyActivationSettings?.mode === ALERT_MANAGER_ACTIVATION_MODES.TOGGLE;
  const match = window.match(/^(\d+)(h|m|s)$/);
  let timeWindow = (match && match[1]) || 0;
  let shortTimeUnit = (match && match[2]) || 'm';

  if (shortTimeUnit === 's') {
    timeWindow /= 60;

    if (timeWindow >= 60) {
      timeWindow /= 60;
      shortTimeUnit = 'h';
    }
  }

  const defaultActivate = {
    timeWindow: Number(timeWindow) === 0 ? 2 : Number(timeWindow),
    timeUnit: shortTimeUnit === 'h' ? 'hour' : 'minute',
    times: Number(matchThreshold || 0) === 0 ? 5 : Number(matchThreshold),
    gracePeriod: deserializeGracePeriod(inactivityTimeUntilClear)
  };

  // In case there's no rule threshold, slightly alter the default activate values under certain circumstances
  if (!ruleLevel && defaultActivate.timeUnit === 'minute' && defaultActivate.timeWindow < defaultActivate.times) {
    defaultActivate.timeWindow = 2;
    defaultActivate.times = 5;
    defaultActivate.timeUnit = 'hour';
  }

  const toggleModeActivate = { timeUnit: 'minute', times: 1, timeWindow: 1, gracePeriod: 5 };

  return {
    ...restThreshold,
    activate: isToggleModePolicy ? toggleModeActivate : defaultActivate
  };
}

function deserializeThresholdConditions(threshold, policy) {
  // direction and fallback settings are in a baseline condition if found
  const baselineCondition = threshold.conditions.find((t) =>
    [THRESHOLD_CONDITION_TYPES.BASELINE, THRESHOLD_CONDITION_TYPES.BASELINE_PERCENT].includes(t.type)
  );
  const defaultCondition = threshold.conditions[0] || {};

  if (baselineCondition) {
    const { fallbackSettings } = baselineCondition;

    if (fallbackSettings) {
      threshold.fallbackSettings = fallbackSettings;
    }
  }

  // Coerce invalid 'thresholdDirectionNone' to default 'currentToHistory' for direction
  threshold.direction =
    defaultCondition.direction === COMPARISON_DIRECTIONS.TO_CURRENT
      ? COMPARISON_DIRECTIONS.TO_CURRENT
      : COMPARISON_DIRECTIONS.TO_HISTORY;

  threshold.keySettings = defaultCondition.keySettings || {};

  const conditions = threshold.conditions.map((condition) => {
    const transformedCondition = {
      operator: condition.operator || 'greaterThanOrEquals',
      comparisonValue: condition.comparisonValue,
      comparisonValueFactor: 1,
      type: condition.type
    };

    if (condition.type === THRESHOLD_CONDITION_TYPES.RATIO) {
      transformedCondition.operator = '';
      transformedCondition.ratioSettings = condition.ratioSettings || {};
      transformedCondition.ratioSettings.direction =
        transformedCondition.ratioSettings.direction || RATIO_CONDITION_DIRECTIONS.LEFT;
      transformedCondition.ratioSettings.lhsMetricPosition = condition.ratioSettings?.lhsMetricPosition || 0;
      transformedCondition.ratioSettings.rhsMetricPosition = condition.ratioSettings?.rhsMetricPosition || 0;
    }

    if (condition.type === THRESHOLD_CONDITION_TYPES.CAPACITY) {
      transformedCondition.comparisonValue = condition.comparisonValue / 10 ** 6;
      transformedCondition.comparisonValueFactor = 10 ** 6;
    }

    if (transformedCondition.type === THRESHOLD_CONDITION_TYPES.STATIC) {
      const metricIndex = policy.metrics.indexOf(condition.metric);

      if (GREEK_METRICS.includes(condition.metric)) {
        const { adjusted, magnitude } = greekIt(transformedCondition.comparisonValue);
        transformedCondition.comparisonValue = adjusted;
        transformedCondition.comparisonValueFactor = magnitude;
      }
      transformedCondition.metric = condition.metric;
      transformedCondition.value_select = `metric_${metricIndex}`;
    }

    if (
      [THRESHOLD_CONDITION_TYPES.BASELINE, THRESHOLD_CONDITION_TYPES.BASELINE_PERCENT].includes(
        transformedCondition.type
      )
    ) {
      transformedCondition.value_select = 'metric_0';
    }

    if (transformedCondition.type === THRESHOLD_CONDITION_TYPES.BASELINE_PERCENT) {
      /**
       * WARNING: Baseline Percent conditions are tricky, proceed carefully!
       *
       * In UI-APP:
       * - baselinePercent values are stored as % of whole, but input by users as a % increase or % decrease.
       * - For currentToHistory, a user input of "20% above baseline" is converted to 120% before being sent to the API.
       * - For historyToCurrent, a user input of "20% below baseline" is converted to 125% before being sent to the API.
       * - The difference is because a 20% decrease in X => Y corresponds to a 25% increase from Y => X. Then the value is converted to a percentage of whole.
       * - While UI allows users to specify % above/below the baseline, chf-activate actually swaps the baseline & current values when doing its comparison.
       * - This is a workaround for the lack of a less-than feature in chf-activate.
       *
       * In CHF-ACTIVATE:
       * - chf-activate compares two sets of traffic data for a given policy ID: current data, and baseline data.
       * - It iterates over each entry in the data sets to see if alarm needs to be triggered.
       * - If threshold is current_to_history, it checks if the current[i] is X% greater than baseline[i].
       * - Example: a current_to_history baselinePercent with a value of 120%: a baseline[i] of 100 and a current[i] of 121 will trigger an alarm.
       * - If threshold is history_to_current, it checks if baseline[i] is X% greater than current[i].
       * - Example: a history_to_current with a baselinePercent of 120%: a current[i] of 96 and a baseline[i] of 120 will trigger an alarm.
       *
       * In the FUTURE:
       * - chf-activate should take a simple positive or negative value and make comparisons accordingly.
       */
      transformedCondition.comparisonValue -= 100;
      if (threshold.direction === COMPARISON_DIRECTIONS.TO_CURRENT) {
        transformedCondition.comparisonValue = deserializeBaselinePercentValue(transformedCondition.comparisonValue);
      }
    }

    // UpDown policies only use boolean operators
    // This handles legacy policy operators.
    if (
      policy.applicationMetadata?.type === 'state-change' &&
      !['equals', 'notEquals'].includes(transformedCondition.operator)
    ) {
      transformedCondition.operator = 'equals';
    }

    return transformedCondition;
  });

  return {
    ...threshold,
    conditions
  };
}

// DESERIALIZE END

// SERIALIZE START

const thresholdPropsToKeepOnSerialize = [
  'thresholdID',
  'policy_id',
  'severity',
  'conditions',
  'activate',
  'ackRequired',
  'description',
  'direction',
  'fallbackSettings',
  'filters',
  'mitigations',
  'keySettings',
  'notificationChannels'
];

function pruneThresholdProperties(threshold) {
  return Object.keys(threshold).reduce((acc, item) => {
    if (thresholdPropsToKeepOnSerialize.includes(item)) {
      acc[item] = threshold[item];
    }

    return acc;
  }, {});
}

function serializeThresholdProperties(threshold) {
  const { policy_id, policyID, thresholdID, ...restThreshold } = threshold;
  const transformedThreshold = {
    ...restThreshold,
    policyID: policyID || policy_id
  };

  if (thresholdID) {
    transformedThreshold.thresholdID = thresholdID;
  }

  return transformedThreshold;
}

function serializeThresholdActivate(threshold) {
  const { activate = {}, ...restThreshold } = threshold || {};

  return {
    ...restThreshold,
    activationSettings: {
      matchThreshold: `${activate.times || 0}`,
      window: transformToSeconds(`${activate.timeWindow || 0}${activate.timeUnit === 'hour' ? 'h' : 'm'}`),
      inactivityTimeUntilClear: transformToSeconds(`${activate.gracePeriod || 0}m`)
    }
  };
}

function serializeThresholdConditions(threshold, policy) {
  const { direction, fallbackSettings, keySettings: thresholdKeySettings = {}, ...restThreshold } = threshold;
  const [primaryMetric] = policy.metrics;
  let keySettings = null;

  const currentKeysChanged =
    thresholdKeySettings?.nTopKeysEvaluated > 0 && thresholdKeySettings?.nTopKeysEvaluated < policy.nTopKeysEvaluated;
  const baselineKeysChanged =
    thresholdKeySettings?.nTopKeysStoredInBaseline > 0 &&
    thresholdKeySettings?.nTopKeysStoredInBaseline < policy.nTopKeysAddedToBaseline;

  // Use keySettings if nonzero & differ from policy settings
  if (currentKeysChanged || baselineKeysChanged) {
    keySettings = {
      nTopKeysEvaluated: thresholdKeySettings.nTopKeysEvaluated || policy.nTopKeysEvaluated,
      nTopKeysStoredInBaseline: thresholdKeySettings.nTopKeysStoredInBaseline || policy.nTopKeysAddedToBaseline
    };
  }

  const conditions = threshold.conditions.map((condition) => {
    const transformedCondition = {
      operator: condition.operator || 'greaterThanOrEquals',
      comparisonValue: condition.comparisonValue,
      type: condition.type,
      direction,
      keySettings
    };

    if (condition.type === THRESHOLD_CONDITION_TYPES.TOP_KEYS) {
      delete transformedCondition.operator;
      delete transformedCondition.comparisonValue;
    }

    if (condition.type === THRESHOLD_CONDITION_TYPES.BASELINE) {
      transformedCondition.metric = primaryMetric;
    }

    // add threshold fallbackSettings to all baseline conditions
    if (
      [THRESHOLD_CONDITION_TYPES.BASELINE, THRESHOLD_CONDITION_TYPES.BASELINE_PERCENT].includes(condition.type) &&
      fallbackSettings
    ) {
      transformedCondition.fallbackSettings = fallbackSettings;
    }

    if (condition.type === THRESHOLD_CONDITION_TYPES.BASELINE_PERCENT) {
      /**
       * WARNING: Baseline Percent conditions are tricky! See comment above before editing.
       */
      if (threshold.direction === COMPARISON_DIRECTIONS.TO_CURRENT) {
        transformedCondition.comparisonValue = serializeBaselinePercentValue(transformedCondition.comparisonValue);
      }

      transformedCondition.comparisonValue += 100;
    }

    if (condition.type === THRESHOLD_CONDITION_TYPES.RATIO) {
      transformedCondition.ratioSettings = condition.ratioSettings || {};
      transformedCondition.ratioSettings.lhsMetricPosition = parseInt(
        transformedCondition.ratioSettings.lhsMetricPosition
      );
      transformedCondition.ratioSettings.rhsMetricPosition = parseInt(
        transformedCondition.ratioSettings.rhsMetricPosition
      );
      transformedCondition.ratioSettings.margin = parseFloat(transformedCondition.ratioSettings.margin);
    }

    if (condition.type === THRESHOLD_CONDITION_TYPES.CAPACITY) {
      transformedCondition.comparisonValue = condition.comparisonValue * condition.comparisonValueFactor;
    }

    if (transformedCondition.type === THRESHOLD_CONDITION_TYPES.STATIC) {
      const [, metricIndex] = condition.value_select.split('_');
      const metric = policy.metrics[parseInt(metricIndex)];
      transformedCondition.metric = metric || condition.metric;

      // Only multiply by comparisonValueFactor for greek metrics
      if (GREEK_METRICS.includes(transformedCondition.metric)) {
        transformedCondition.comparisonValue = `${
          transformedCondition.comparisonValue * condition.comparisonValueFactor
        }`;
      }
    }

    return transformedCondition;
  });

  return {
    ...restThreshold,
    conditions
  };
}

function serializeThresholdMitigations(threshold) {
  const { mitigations, ...restThreshold } = threshold || {};

  const newMitigations = (mitigations || []).map((mitigation) => {
    const { uiProperties } = mitigation || {};
    const {
      applyTimeoutValue = 0,
      applyTimeoutFactor = 1,
      clearTimeoutValue = 0,
      clearTimeoutFactor = 1
    } = uiProperties || {};

    if (mitigation.applyStrategy !== 'userAckOrTimeout') {
      delete mitigation.applyTimeout;
    } else {
      // add time unit, protobuf needs it
      mitigation.applyTimeout = transformToSeconds(`${applyTimeoutValue * applyTimeoutFactor}m`);
    }

    if (mitigation.clearStrategy !== 'userAckOrTimeout') {
      delete mitigation.clearTimeout;
    } else {
      // add time unit, protobuf needs it
      mitigation.clearTimeout = transformToSeconds(`${clearTimeoutValue * clearTimeoutFactor}m`);
    }

    delete mitigation.uiProperties;
    return mitigation;
  });

  return {
    ...restThreshold,
    mitigations: newMitigations
  };
}

function deserialize(policy) {
  const { rule = {} } = policy || {};
  // careful here, a little misleading: the call chaining actually mutates the object and then returns it so ..
  // the same object is passed through the chain, should be curried instead but for now it's fine, perhaps TODO?
  const thresholds = (policy.thresholds || []).map((threshold) => {
    const ruleLevel = (rule?.levels || []).find(
      (level) => ALERT_MANAGER_SEVERITY_TO_STANDARD_ALERT_SEVERITY[level.severity] === threshold.severity
    );

    const transformedThresholdProperties = deserializeThresholdProperties(threshold, ruleLevel);
    const transformedActivate = deserializeThresholdActivate(transformedThresholdProperties, policy, ruleLevel);
    const transformedMitigations = deserializeThresholdMitigations(transformedActivate);

    return deserializeThresholdConditions(transformedMitigations, policy);
  });

  return {
    ...policy,
    thresholds
  };
}

// SERIALIZE END

function serialize(policy, removeMitigations) {
  const thresholds = (policy.thresholds || [])
    .filter((threshold) => threshold.enabled)
    .map((threshold) => {
      const prunedThreshold = pruneThresholdProperties(threshold);
      const serializedThresholdProperties = serializeThresholdProperties(prunedThreshold);
      const serializedActivate = serializeThresholdActivate(serializedThresholdProperties);
      const serializedConditions = serializeThresholdConditions(serializedActivate, policy);
      const serializedThreshold = serializeThresholdMitigations(serializedConditions, policy);

      if (serializedThreshold.notificationChannels && Array.isArray(serializedThreshold.notificationChannels)) {
        serializedThreshold.notificationChannels = serializedThreshold.notificationChannels.map(
          (notificationChannel) =>
            typeof notificationChannel === 'object' && notificationChannel !== null
              ? notificationChannel.id
              : notificationChannel
        );
      }

      if (removeMitigations || serializedThreshold.mitigations === '') {
        delete serializedThreshold.mitigations;
      }

      if (!serializedThreshold.filters) {
        serializedThreshold.filters = null;
      }

      // Enable baseline backfill if an enabled policy has a baseline condition
      if (
        policy.status === 'active' &&
        Array.isArray(serializedThreshold?.conditions) &&
        serializedThreshold.conditions.some(({ type }) =>
          [
            THRESHOLD_CONDITION_TYPES.BASELINE,
            THRESHOLD_CONDITION_TYPES.BASELINE_PERCENT,
            THRESHOLD_CONDITION_TYPES.TOP_KEYS
          ].includes(type)
        )
      ) {
        policy.baselineBackfillImmediately = true;
      }

      return serializedThreshold;
    });

  return {
    ...policy,
    thresholds
  };
}

export default {
  serialize,
  deserialize,

  // Exported for testing
  deserializeThresholdProperties,
  deserializeThresholdConditions,
  deserializeThresholdMitigations,
  deserializeThresholdActivate,
  serializeThresholdProperties,
  serializeThresholdConditions,
  serializeThresholdMitigations,
  serializeThresholdActivate
};
